/**
 * What a game screen holds for as long as it is mounted, solo or duel: the
 * screen wake lock, and the page-visibility handling around it.
 *
 * When the page is hidden the whole audio context suspends, not just the
 * music gain, so a hidden tab schedules nothing at all; it resumes when the
 * page is shown again. The screen's own reaction (`onVisibility`) runs first
 * and synchronously, on the event itself: a duel has to tell the server the
 * page is going away *before* it goes, because iOS freezes a hidden page
 * within seconds and there is no later opportunity.
 *
 * Starting the music is the screen's own decision (a solo run starts it when
 * the run is running, a room for as long as it is open); stopping it on the
 * way out is done here, so a screen cannot leave it playing over the menus.
 */
import { audio } from '../audio/engine.js';
import { WakeLockController } from '../pwa/wakelock.js';

export interface PlayScreen {
  /** Lets the screen sleep again before the screen is left: a finished run. */
  releaseWakeLock(): void;
  /** Call from the screen's `onMount` cleanup. */
  destroy(): void;
}

export function startPlayScreen(onVisibility: (hidden: boolean) => void): PlayScreen {
  const wakeLock = new WakeLockController();

  function onVisibilityChange(): void {
    const hidden = document.visibilityState === 'hidden';
    onVisibility(hidden);
    wakeLock.handleVisibilityChange(document.visibilityState);
    if (hidden) void audio.suspend();
    else void audio.resume();
  }

  document.addEventListener('visibilitychange', onVisibilityChange);
  void wakeLock.acquire();

  return {
    releaseWakeLock: () => {
      void wakeLock.release();
    },
    destroy: () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      void wakeLock.release();
      audio.stopMusic();
    },
  };
}
